export type TaskSortField =
  | 'created'
  | 'dueDate'
  | 'estimatedValue'
  | 'resolvedValue'
  | 'status'
  | 'title';

export type TaskSortOrder = 'asc' | 'desc';

/** `TaskService.findAll`'s sort names; a typo would silently sort on nothing. */
type TaskApiSortKey =
  | 'created'
  | 'dueDate'
  | 'estimatedValue'
  | 'resolved'
  | 'resolvedValue'
  | 'statusRank'
  | 'title';

type TaskApiSort = `${TaskApiSortKey}:${TaskSortOrder}`;

export interface TaskSortSelection {
  field: TaskSortField;
  order: TaskSortOrder;
}

export interface TaskTabSort {
  default: TaskSortSelection;
  /** Offered in the field dropdown, in this order. */
  fields: TaskSortField[];
  /** Applied ahead of the member's choice, e.g. resolved claims last. */
  pinned?: TaskApiSort[];
}

/**
 * A link or choice carrying a value field the sort doesn't offer means the
 * other one: Est. Value kept when a competing-claims list is narrowed to
 * Resolved becomes Est. Resolved Value, and back. A mixed list offers both,
 * so an exact match wins (`useTaskSort`).
 */
const VALUE_SORT_FIELDS: TaskSortField[] = ['estimatedValue', 'resolvedValue'];

export const isSameTaskSortField = (
  field: TaskSortField,
  param: null | string,
) =>
  field === param ||
  (VALUE_SORT_FIELDS.includes(field) &&
    VALUE_SORT_FIELDS.some((valueField) => valueField === param));

/**
 * `sortByKey` is the task list's `sortBy` name. `defaultOrder` is applied when
 * the member picks the field, so value and recency lead with the largest and
 * newest. `tiebreaks` keep a fixed direction whichever way the member sorts,
 * ending in `created` so paging stays stable. `isUnpinned` drops the tab's
 * pinned keys for a field that is only ever set on the claims they pin.
 */
export const TASK_SORT_FIELDS: Record<
  TaskSortField,
  {
    defaultOrder: TaskSortOrder;
    isUnpinned?: boolean;
    label: string;
    sortByKey: TaskApiSortKey;
    tiebreaks: TaskApiSort[];
  }
> = {
  created: {
    defaultOrder: 'desc',
    label: 'Date Created',
    sortByKey: 'created',
    tiebreaks: ['title:asc'],
  },
  dueDate: {
    defaultOrder: 'asc',
    label: 'Due Date',
    sortByKey: 'dueDate',
    tiebreaks: ['estimatedValue:desc', 'title:asc', 'created:asc'],
  },
  estimatedValue: {
    defaultOrder: 'desc',
    label: 'Est. Value',
    sortByKey: 'estimatedValue',
    tiebreaks: ['title:asc', 'created:asc'],
  },
  // Only resolved claims carry it, so pinning them last would bury the very
  // claims it orders behind open ones that tie on blank (APP-2631).
  resolvedValue: {
    defaultOrder: 'desc',
    isUnpinned: true,
    label: 'Est. Resolved Value',
    sortByKey: 'resolvedValue',
    tiebreaks: ['title:asc', 'created:asc'],
  },
  status: {
    defaultOrder: 'asc',
    label: 'Status',
    sortByKey: 'statusRank',
    tiebreaks: ['title:asc', 'created:asc'],
  },
  title: {
    defaultOrder: 'asc',
    label: 'Alphabetical',
    sortByKey: 'title',
    tiebreaks: ['created:asc'],
  },
};

export const TASK_SORT_ORDER_LABELS: Record<TaskSortOrder, string> = {
  asc: 'Ascending',
  desc: 'Descending',
};

export const TASK_SORT_PARAMS = {
  field: 'tasksSort',
  order: 'tasksOrder',
} as const;

export const isTaskSortOrder = (value: null | string): value is TaskSortOrder =>
  value === 'asc' || value === 'desc';

export const buildTaskSortBy = (
  sort: TaskTabSort,
  { field, order }: TaskSortSelection,
) => {
  const { isUnpinned, sortByKey, tiebreaks } = TASK_SORT_FIELDS[field];
  const pinned = isUnpinned ? [] : (sort.pinned ?? []);

  return [...pinned, `${sortByKey}:${order}`, ...tiebreaks].join(',');
};
